import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Field } from "@/pages/Income";
import { CURRENCIES } from "@/lib/currency";
import AppearanceSection from "@/components/settings/AppearanceSection";
import LanguageSection from "@/components/settings/LanguageSection";
import WeeklyRecapCard from "@/components/settings/WeeklyRecapCard";
import WallOfWinsCard from "@/components/settings/WallOfWinsCard";
import SubscriptionCard from "@/components/billing/SubscriptionCard";

export default function Settings() {
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [target, setTarget] = useState(user?.monthly_income_target ?? "");
  const [recapOptOut, setRecapOptOut] = useState(!!user?.weekly_recap_opt_out);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/settings", {
        currency,
        monthly_income_target: target === "" ? null : parseFloat(target),
        weekly_recap_opt_out: recapOptOut,
      });
      setUser(data);
      toast.success(t("common.toast_saved"));
    } catch (e) {
      console.error("Settings save failed:", e);
      toast.error(t("common.toast_failed"));
    } finally {
      setSaving(false);
    }
  };

  const resetAccount = async () => {
    const confirm1 = confirm(
      "Reset your account?\n\n" +
      "This will PERMANENTLY delete all your income, expenses, loans, subscriptions, " +
      "bills, goals, investments, budgets, streak history, Plaid connections, chat " +
      "history, and notifications.\n\n" +
      "Your profile (email, name, currency, settings) will be kept.\n\n" +
      "This cannot be undone."
    );
    if (!confirm1) return;
    const typed = prompt('Type "RESET" (all caps) to confirm.');
    if (typed !== "RESET") { toast.info(t("toasts.reset_cancelled")); return; }
    setResetting(true);
    try {
      const { data } = await api.post("/auth/reset-account");
      const total = Object.values(data.deleted || {}).reduce((a, n) => a + n, 0);
      toast.success(`Account reset — ${total} rows deleted`);
      setTimeout(() => window.location.assign("/dashboard"), 800);
    } catch (e) {
      console.error("Reset failed:", e);
      toast.error(t("toasts.reset_failed"));
    } finally {
      setResetting(false);
    }
  };

  const restartTour = () => {
    try { localStorage.removeItem("pfo_tour_completed"); }
    catch (e) { console.warn("Settings: localStorage clear failed", e); }
    toast.success(t("toasts.tour_replay"));
  };

  return (
    <div>
      <PageHeader subtitle={t("settings.title")} title={t("settings.subtitle")}/>

      {/* Subscription — full-width row on top of the settings grid */}
      <div className="mb-5">
        <div className="label-eyebrow mb-3">{t("subscription.section_title")}</div>
        <SubscriptionCard variant="full" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Profile */}
        <div className="card-flat p-6">
          <div className="label-eyebrow mb-4">{t("settings.profile")}</div>
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover border border-[var(--border-default)]"/>
            ) : (
              <div className="h-16 w-16 rounded-full bg-[var(--bg-accent)] flex items-center justify-center text-2xl text-[var(--accent-primary)] font-semibold">
                {user?.name?.[0]}
              </div>
            )}
            <div>
              <div className="text-lg font-medium">{user?.name}</div>
              <div className="text-sm text-[var(--text-muted)]">{user?.email}</div>
            </div>
          </div>
          <button className="btn-ghost mt-6" onClick={logout} data-testid="settings-logout-btn">{t("common.sign_out")}</button>
        </div>

        {/* Preferences */}
        <div className="card-flat p-6">
          <div className="label-eyebrow mb-4">{t("settings.preferences")}</div>
          <div className="grid gap-4">
            <Field label={t("common.currency")}>
              <select className="field-input" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="settings-currency">
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{`${c.symbol} · ${c.code} — ${c.label}`}</option>
                ))}
              </select>
            </Field>
            <Field label={t("settings.monthly_income_target", { currency })}>
              <input type="number" step="0.01" className="field-input" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="settings-target"/>
            </Field>
            <div className="flex justify-end">
              <button className="btn-primary" onClick={save} disabled={saving} data-testid="settings-save-btn">
                {saving ? t("common.saving") : t("common.save_changes")}
              </button>
            </div>
          </div>
        </div>

        <LanguageSection t={t} />

        <AppearanceSection />

        {/* Data */}
        <div className="card-flat p-6">
          <div className="label-eyebrow mb-4">{t("settings.data")}</div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {t("settings.data_hint")}
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <button className="btn-danger-ghost" onClick={resetAccount} disabled={resetting} data-testid="settings-reset-btn">
              {resetting ? t("settings.resetting") : t("settings.reset_account")}
            </button>
            <button className="btn-ghost" onClick={restartTour} data-testid="settings-restart-tour-btn">
              {t("settings.replay_tour")}
            </button>
          </div>
        </div>

        <WeeklyRecapCard
          recapOptOut={recapOptOut}
          setRecapOptOut={setRecapOptOut}
          saving={saving}
          parentSave={save}
          t={t}
        />

        <WallOfWinsCard t={t} />
      </div>
    </div>
  );
}
